'use client';

/**
 * ObeyaCostPlanComparisonCard — vendor-comparison cross-tab: one row per
 * schedule requirement, one column per vendor, cell is that vendor's own
 * real pick (name + price) for that requirement. Quantity is its own
 * column, row-level, not repeated per cell — it's a property of the
 * requirement (every alternative prices the same demand), not of any
 * one vendor's pick. Read-only for now — no editing surfaced here.
 *
 * New, additive tab — not a change to ObeyaCostPlanLinesCard's existing
 * Line/Slot/Vendor pivot. That pivot groups by product_sid (Line mode)
 * and sums qty/value across a group; several competing vendor picks for
 * one requirement are ALTERNATIVES, not additional units, so summing
 * them (as a group total, or across this grid's own row) would be
 * exactly the ambiguity this session found and deliberately did not try
 * to resolve here — see queries.build_costplan_comparison's docstring
 * and docs/wip.md. This card shows the comparison; it never computes a
 * total across a row's cells.
 *
 * Self-fetches costplan_comparison — a dedicated strategy, not
 * costplan_pivot reshaped client-side (no grouping/aggregation logic
 * lives in this component; the BFF ships rows/vendor_columns ready to
 * paint).
 *
 * @see klyde_edge/ui_config/composition/cards/obeya.py — ObeyaCostPlanComparisonCard
 * @see klyde_edge/klyde_intelligence/projects/queries.py — build_costplan_comparison
 *
 * @component_role: Card
 * @component_type: Card Renderer
 * @domain: Canvas / Obeya
 * @layer: presentation
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import { FileText, Circle, CircleCheck, ChevronLeft, ChevronRight } from 'lucide-react';
import { text, status } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { useStrategyData, useMcpLineSelect } from '@/lib/api/hooks';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { usePinnedCostPlanStore } from '@/lib/stores/pinned-costplan-store';
import { useCartStore } from '@/lib/stores/cart-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useCardExport } from '@/lib/design/card-export';
import { useCardIdentity } from '@/lib/design/card-chrome';

interface ComparisonPick {
  line_id: string | null; product_sid: string | null; product_name: string | null;
  product_sku: string | null; image_url: string | null;
  unit_price: number | null; quantity: number | null; spec: string | null;
  // A real VendorResponseLine match, when one exists — reference only,
  // never substituted for unit_price (the plan's own entered price
  // stays the plan's own entered price). When present, the UI promotes
  // this as the emphasized number and demotes unit_price to a small,
  // labeled "List" figure beside it — the quoted price needs no label
  // of its own, only the reference figure does.
  reaches_quote: boolean; quoted_unit_price: number | null;
  // The VendorResponse this pick's price came from — null unless
  // reaches_quote is true. A pointer, not a price.
  quoted_response_sid: string | null;
  // Whether THIS pick is currently the requirement's winning selection
  // — a plain lookup against a SELECTED edge server-side (queries.
  // load_costplan_selections), never derived from anything on the pick
  // itself. At most one true per row; absent everywhere in a row means
  // genuinely undecided, not an error. See klyde_edge/docs/
  // mcp_engine_concept.md, "A real gap found building this."
  is_selected: boolean;
  // What the line would cost to switch to this pick: (its price - the selected price) x the
  // requirement's quantity. Signed, from the BFF; null when it can't be said.
  switch_delta: number | null;
}
interface ComparisonRow {
  key: string; label: string;
  // Two different schedule lines can share the same generic label
  // (e.g. two "Occasional chair" requirements in different rooms) —
  // genuinely two rows, not a duplicate. room_label disambiguates
  // when the line has a room placement; null otherwise (not yet
  // placed — same "describe what's there, don't guess" rule as
  // Room Comparison).
  room_label: string | null;
  // The requirement's own quantity — a property of the row, not any
  // one vendor's pick, since every alternative prices the same
  // demand. null when this row's picks actually disagree on quantity
  // (see quantity_mismatch) — a real discrepancy, never silently
  // resolved to one of the conflicting values.
  quantity: number | null; quantity_mismatch: boolean;
  // Keyed by vendor_sid — matches vendor_columns entries. A vendor with
  // no pick for this requirement is simply absent, not a null value —
  // an empty cell, never a zero.
  picks: Record<string, ComparisonPick>;
  // The market context for this requirement — resolved server-side
  // (costplan_line_items_from_graph_rows), never computed here.
  // indicative_price_source names which kind of fact this is: "wish"
  // (an asserted buyer target) or "benchmark" (a derived market
  // fallback when no target was ever set) — always label them
  // differently, never present them identically. Absent (both null)
  // when neither resolves. See docs/mcp_pricing_benchmark.md.
  indicative_price_min: number | null;
  // The market's mid-tier point — only ever set when
  // indicative_price_source is "benchmark". A "wish" target is a real
  // range (two points); a benchmark is three concrete segments
  // (budget/mid/premium), not a spread — this is what tells the
  // renderer which shape to draw, not indicative_price_source alone.
  indicative_price_mid: number | null;
  indicative_price_max: number | null;
  indicative_price_source: 'wish' | 'benchmark' | null;
  // Set server-side (build_costplan_comparison) only on the first row
  // of a products/services group, and only when the plan actually has
  // both — a plan with no services keeps this null on every row, same
  // flat list as before. Render a header exactly where this is set,
  // nowhere else — the BFF already decided the grouping and order.
  section_label: string | null;
}
type RfqState = 'none' | 'draft' | 'awaiting_quote' | 'quoted' | 'cancelled';
// Read from the Plan Lenses facts by the BFF (comparison_stats.py) — nothing here is counted or derived.
interface VendorStats {
  selected_count: number; selected_spend: number;
  open_requirements_reached: number; open_requirements_quoted: number;
  rfq_state: RfqState; days_to_deadline: number | null;
}
interface VendorColumn { vendor_sid: string; vendor_name: string; stats: VendorStats | null; default_expanded: boolean }
interface ComparisonTotals {
  approved_budget: number | null; selected_spend: number; budget_left_after_selected: number | null;
  requirements_total: number; requirements_selected: number; requirements_open: number;
}
interface ComparisonData {
  rows: ComparisonRow[]; vendor_columns: VendorColumn[]; row_count: number;
  totals?: ComparisonTotals;
  labels?: Record<string, string>;
}

const money = (v: number) => `$${v.toLocaleString()}`;
const RFQ_DOT: Record<RfqState, string> = {
  quoted: 'bg-emerald-500', awaiting_quote: 'bg-amber-500', draft: 'bg-muted-foreground/40', none: 'bg-muted-foreground/40', cancelled: 'bg-muted-foreground/40',
};
// The price a pick is shown at: the vendor's quote when it has one, else the plan's own.
const priceOf = (p: ComparisonPick) => (p.reaches_quote && p.quoted_unit_price != null ? p.quoted_unit_price : p.unit_price);
const RFQ_TONE: Record<RfqState, string> = {
  quoted: status.pos, awaiting_quote: status.warn, draft: status.off, none: status.off, cancelled: status.off,
};

export interface ObeyaCostPlanComparisonCardProps {
  /**
   * Explicit fetch-input override — same card, parameterized for a
   * second surface (mental move 6: a self-fetching card takes its
   * fetch inputs as live props, never frozen internal state). When
   * set, this wins over `useViewportSelection()`'s page selection —
   * mirrors `ObeyaCostPlanLinesCard`'s `mcpIdOverride` for consistency.
   * Omit to keep the original mode1 behaviour (reads page selection via
   * useViewportSelection).
   */
  mcpIdOverride?: string | null;
  /**
   * Chrome-only sizing knob for the two frozen columns (Requirement,
   * Selected) — never the vendor columns, never behavior. Mode1's
   * canvas tab has a full-width table and wants today's exact
   * rendering (default, unchanged); the narrower Cost Plan review
   * overlay wants the frozen columns to give up space back to the
   * vendor columns it exists to compare. Same capability either way —
   * this only picks which chrome the frozen columns wear.
   */
  density?: 'compact' | 'comfortable';
}

export function ObeyaCostPlanComparisonCard({ mcpIdOverride, density = 'comfortable' }: ObeyaCostPlanComparisonCardProps = {}) {
  const compact = density === 'compact';
  // Two fixed widths per density — not computed/interpolated — so
  // Tailwind's static scan sees both literal class strings in source.
  const reqColCls = compact ? 'w-[150px] min-w-[150px] max-w-[150px]' : 'w-[220px] min-w-[220px] max-w-[220px]';
  const selLeftCls = compact ? 'left-[150px]' : 'left-[220px]';
  // Requirement is never sticky-dependent on Selected's width — only
  // Requirement and Selected are sticky at all; vendor columns scroll
  // normally — so collapsing Selected never has to recompute anything
  // downstream, unlike a vendor column collapse sitting between other
  // sticky columns would.
  const viewportSelection = useViewportSelection();
  // Fallback chain: explicit override prop > page selection > the
  // pinned-costplan-store's live pin > the composer's own targeting
  // state (editingMcpSid, set either by the heavy loadMcpForEdit
  // reload or the lightweight targetMcpForAppend pick). Lets this card
  // mount via the generic matrix-driven LayoutEngine (which passes no
  // per-instance props) inside the Cost Plan review overlay and still
  // resolve the pinned plan, OR the plan the Composer is currently
  // targeting when opened from there — see composer-lines-card.tsx's
  // "Compare across vendors" trigger. Harmless for mode1's existing
  // canvas usage: viewportSelection.mcpId is always set there, so
  // neither fallback link fires.
  const pinnedMcpId = usePinnedCostPlanStore((s) => s.pinnedMcpId);
  const editingMcpSid = useCartStore((s) => s.editingMcpSid);
  const mcpId = mcpIdOverride ?? viewportSelection.mcpId ?? pinnedMcpId ?? editingMcpSid;
  const selectNode = useGraphSelectionStore((s) => s.selectNode);
  const selectedId = useGraphSelectionStore((s) => s.selectedNode?.id);
  const { contentKey } = useCardIdentity();

  const strategy = useCapabilityStore((s) => s.strategyName('cost_plans', 'comparison'));
  const selectStrategy = useCapabilityStore((s) => s.strategyName('cost_plans', 'line_select'));

  const { data, isLoading } = useStrategyData<ComparisonData>(
    strategy, { semantic_id: mcpId ?? '' }, !!mcpId && !!strategy,
  );
  const setSelection = useMcpLineSelect(selectStrategy);

  const rows = data?.data?.rows ?? [];
  const vendorColumns = data?.data?.vendor_columns ?? [];
  const totals = data?.data?.totals;
  const L = data?.data?.labels ?? {};

  // Which vendor columns are open. The BFF says which start open (`default_expanded`); a choice made
  // here is remembered per plan, in this browser only.
  const storageKey = mcpId ? `cost-comparison-columns:${mcpId}` : null;
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  useEffect(() => {
    if (!storageKey) return;
    try { setOverrides(JSON.parse(window.localStorage.getItem(storageKey) ?? '{}')); } catch { setOverrides({}); }
  }, [storageKey]);
  const remember = (next: Record<string, boolean>) => {
    setOverrides(next);
    try { if (storageKey) window.localStorage.setItem(storageKey, JSON.stringify(next)); } catch { /* storage unavailable: choice lasts this visit */ }
  };
  const isOpen = (vc: VendorColumn) => overrides[vc.vendor_sid] ?? vc.default_expanded;
  const toggleColumn = (vc: VendorColumn) => remember({ ...overrides, [vc.vendor_sid]: !isOpen(vc) });
  const allOpen = vendorColumns.every(isOpen);
  const toggleAll = () => remember(allOpen ? {} : Object.fromEntries(vendorColumns.map((v) => [v.vendor_sid, true])));

  // Selected's own collapse rides the SAME overrides map vendor columns
  // use — a sentinel key standing in for a vendor_sid, not a parallel
  // boolean. Only meaningful in compact density: comfortable (mode1)
  // never collapses it, so selectedCollapsed is always false there
  // regardless of what's in storage. Defaults open (today's behaviour
  // unchanged until the user collapses it once).
  const SELECTED_COL_KEY = '__selected__';
  const selectedColOpen = overrides[SELECTED_COL_KEY] ?? true;
  const toggleSelectedColumn = () => remember({ ...overrides, [SELECTED_COL_KEY]: !selectedColOpen });
  const selectedCollapsed = compact && !selectedColOpen;
  const selColCls = selectedCollapsed
    ? 'w-[40px] min-w-[40px] max-w-[40px]'
    : compact ? 'w-[150px] min-w-[150px] max-w-[150px]' : 'w-[170px] min-w-[170px] max-w-[170px]';

  // Clicking a vendor elsewhere (a product, a vendor chip) opens its column here.
  const selectedNode = useGraphSelectionStore((s) => s.selectedNode);
  useEffect(() => {
    if (selectedNode?.type !== 'vendor') return;
    const vc = vendorColumns.find((v) => v.vendor_sid === selectedNode.id || v.vendor_name === selectedNode.label);
    if (vc && overrides[vc.vendor_sid] !== true) remember({ ...overrides, [vc.vendor_sid]: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNode?.id, selectedNode?.label]);

  // "due in 1 day" / "due in 6 days" / "overdue by 2 days" — the numbers come from the BFF, the words from labels.
  const deadline = (days: number | null) => {
    if (days == null) return '';
    const n = Math.abs(days);
    return ` · ${days < 0 ? L.cmp_overdue : L.cmp_due_in} ${n} ${n === 1 ? L.cmp_day : L.cmp_days}`;
  };

  // Click the radio on the currently-selected pick to clear the row back
  // to undecided; click any other pick's radio to replace the winner —
  // never a toggle-off on an unselected pick, since there's nothing to
  // clear there. One mutation either way; the grid refetches on success.
  const toggleSelection = (pick: ComparisonPick) => {
    if (!pick.line_id || !mcpId) return;
    setSelection.mutate({ semantic_id: mcpId, line_id: pick.line_id, select: !pick.is_selected });
  };

  const selectPick = (pick: ComparisonPick, vendorSid: string, vendorName: string) => {
    if (!pick.product_sid) return;
    selectNode(
      { id: pick.product_sid, label: pick.product_name ?? 'Product', type: 'product', properties: {} },
      undefined,
      { id: vendorSid, label: vendorName, type: 'vendor', properties: {} },
    );
  };

  // Separate target from selectPick — a quoted price and its product
  // are two different things to open, not one click doing double duty.
  const selectVendorResponse = (responseSid: string, vendorName: string) => {
    selectNode({ id: responseSid, label: vendorName, type: 'vendor_response', properties: {} });
  };

  useCardExport(useMemo(() => ({
    content_key: contentKey ?? 'obeya_costplan_comparison',
    status: (data ? 'loaded' : mcpId && strategy ? 'loading' : 'idle') as 'loaded' | 'loading' | 'idle',
    query_key: null,
    text: `Cost comparison: ${rows.length} requirements × ${vendorColumns.length} vendors`,
    json: null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [contentKey, data, mcpId, strategy, rows.length, vendorColumns.length]));

  if (!mcpId) return null;

  return (
    <div className="flex flex-col h-full">
      {/* The plan's headline figures — the same numbers Plan Lenses shows. */}
      <div className="flex flex-wrap gap-x-10 gap-y-2 px-4 py-3 border-b border-border shrink-0">
        {totals && [
          [L.cmp_budget, totals.approved_budget != null ? money(totals.approved_budget) : '—'],
          [L.cmp_selected, money(totals.selected_spend)],
          [L.cmp_left, totals.budget_left_after_selected != null ? money(totals.budget_left_after_selected) : '—'],
          [L.cmp_open, `${totals.requirements_open} ${L.cmp_of} ${totals.requirements_total}`],
        ].map(([label, value]) => (
          <div key={label} className="flex flex-col gap-0.5">
            <span className={cn(text.eyebrow)}>{label}</span>
            <span className={cn(text.value)}>{value}</span>
          </div>
        ))}
        {vendorColumns.length > 0 && (
          <button type="button" onClick={toggleAll}
            className={cn(text.muted, 'ml-auto self-end rounded-md border border-border px-3 py-1 hover:text-foreground')}>
            {allOpen ? L.cmp_collapse_empty : L.cmp_expand_all}
          </button>
        )}
      </div>

      <div className="flex-1 overflow-auto">
        {/* No table-wide w-full and a floor width per vendor column —
            same reasoning as Room Comparison: columns shouldn't
            compress toward unreadable as vendor count grows, the
            table should grow past the viewport and scroll instead. */}
        <table className="text-xs border-collapse">
          <thead className="sticky top-0 z-30">
            <tr>
              <th className={cn('sticky left-0 z-20 bg-card border-b border-r border-border px-3 py-2 text-left', reqColCls)}>
                Requirement
              </th>
              {/* Pinned right after Requirement so the current decision
                  stays visible regardless of how far the vendor columns
                  scroll — a selection made in column 8 would otherwise
                  disappear the moment you scroll back to compare columns
                  1-3. Mirrors is_selected, never a second source of
                  truth for it. */}
              {selectedCollapsed ? (
                // Same collapsed-rail treatment as a collapsed vendor
                // column's header — chevron, vertical label, nothing
                // else — reused, not reinvented.
                <th className={cn('sticky z-20 bg-card border-b border-r border-border p-0 align-top', selLeftCls, selColCls)}>
                  <button type="button" onClick={toggleSelectedColumn} aria-expanded={false} title={L.selected_column ?? 'Selected'}
                    className="group flex w-full cursor-pointer flex-col items-center gap-2 py-2.5 hover:bg-muted/60">
                    <ChevronRight className="h-3.5 w-3.5 text-muted-foreground group-hover:text-foreground" />
                    <span className={cn(text.muted, 'font-medium')} style={{ writingMode: 'vertical-rl' }}>{L.selected_column ?? 'Selected'}</span>
                  </button>
                </th>
              ) : (
                <th className={cn('sticky z-20 bg-card border-b border-r border-border px-3 py-2 text-left', selLeftCls, selColCls)}>
                  <div className="flex items-center justify-between gap-2">
                    <span>{L.selected_column ?? 'Selected'}</span>
                    {/* Collapse affordance only exists in compact
                        density — comfortable (mode1) keeps this column
                        always expanded, no toggle offered. */}
                    {compact && (
                      <button type="button" onClick={toggleSelectedColumn} aria-expanded={true} title={L.selected_column ?? 'Selected'}
                        className="text-muted-foreground hover:text-foreground"><ChevronLeft className="h-4 w-4" /></button>
                    )}
                  </div>
                </th>
              )}
              {/* Qty column hidden for now — quantity lives on two
                  surfaces (the line's own quantity vs. its
                  room_placements partition) that aren't currently
                  reconciled, so showing it here was mostly noise
                  (⚠ on nearly every row). See docs/wip.md. */}
              {vendorColumns.map((vc) => {
                const st = vc.stats;
                if (!isOpen(vc)) {
                  const tip = st
                    ? `${vc.vendor_name} · ${L.cmp_selected_short} ${st.selected_count} · ${L.cmp_reach} ${st.open_requirements_reached} · ${L.cmp_quoted} ${st.open_requirements_quoted} · ${L[`cmp_rfq_${st.rfq_state}`]}`
                    : vc.vendor_name;
                  return (
                    <th key={vc.vendor_sid} className="bg-card border-b border-border p-0 align-top w-[40px] min-w-[40px] max-w-[40px]">
                      <button type="button" onClick={() => toggleColumn(vc)} aria-expanded={false} title={tip}
                        className="group flex w-full cursor-pointer flex-col items-center gap-2 py-2.5 hover:bg-muted/60">
                        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground group-hover:text-foreground" />
                        <span className={cn('h-2 w-2 rounded-full', st ? RFQ_DOT[st.rfq_state] : 'bg-muted-foreground/40')} />
                        <span className={cn(text.muted, 'font-medium')} style={{ writingMode: 'vertical-rl' }}>{vc.vendor_name}</span>
                      </button>
                    </th>
                  );
                }
                return (
                <th key={vc.vendor_sid} className="bg-card border-b border-border px-3 py-2 text-left font-medium min-w-[200px] align-top">
                  <div className="flex items-start justify-between gap-2">
                    <span>{vc.vendor_name}</span>
                    <button type="button" onClick={() => toggleColumn(vc)} aria-expanded={true} title={vc.vendor_name}
                      className="text-muted-foreground hover:text-foreground"><ChevronLeft className="h-4 w-4" /></button>
                  </div>
                  {st && (
                    <div className={cn(text.muted, 'mt-1 flex flex-col gap-0.5 font-normal tabular-nums')}>
                      <span>
                        {L.cmp_selected_short} <b className="text-foreground">{st.selected_count}</b>
                        {st.selected_count > 0 && <> · <b className="text-foreground">{money(st.selected_spend)}</b></>}
                      </span>
                      <span>
                        {L.cmp_reach} <b className="text-foreground">{st.open_requirements_reached}</b>
                        {' · '}{L.cmp_quoted} <b className="text-foreground">{st.open_requirements_quoted}</b>
                      </span>
                      <span className={cn('font-medium', RFQ_TONE[st.rfq_state])}>
                        {L[`cmp_rfq_${st.rfq_state}`]}{st.rfq_state === 'awaiting_quote' && deadline(st.days_to_deadline)}
                      </span>
                    </div>
                  )}
                </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {isLoading && rows.length === 0 ? (
              <tr><td colSpan={vendorColumns.length + 2} className="px-3 py-6 text-center text-muted-foreground">…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={vendorColumns.length + 2} className="px-3 py-6 text-center text-muted-foreground">No lines on this plan yet</td></tr>
            ) : rows.map((row) => {
              const winner = Object.values(row.picks).find((p) => p.is_selected) ?? null;
              return (
              <Fragment key={row.key}>
              {row.section_label && (
                <tr key={`${row.key}-section`} className="bg-muted/40">
                  <td colSpan={vendorColumns.length + 2} className={cn(text.muted, 'px-3 py-1.5 font-medium uppercase tracking-wide')}>
                    {row.section_label}
                  </td>
                </tr>
              )}
              <tr key={row.key} className="border-b border-border">
                <td tabIndex={0} className={cn('group relative sticky left-0 z-10 hover:z-20 focus-within:z-20 bg-card border-r border-border px-3 py-2.5 align-top focus:outline-none', reqColCls)}>
                  {/* 2-line clamp, not single-line truncate — compact
                      still needs to distinguish requirements that share
                      an opening phrase ("Sofa with 2x removable..." vs
                      "...fixed..."), and not unlimited wrap — one long
                      label shouldn't blow out that row's height for
                      every vendor column sharing it. */}
                  <div className={cn(text.body, 'font-medium', compact && 'line-clamp-2')}>{row.label || '—'}</div>
                  {/* Same generic label can belong to two different
                      schedule lines (e.g. two "Occasional chair"
                      requirements in different rooms) — this is what
                      tells them apart. Absent, not "—", when the line
                      has no room placement yet. Dropped in compact —
                      the frozen column only has room for the label. */}
                  {!compact && row.room_label && (
                    <div className={cn(text.muted, 'mt-0.5')}>{row.room_label}</div>
                  )}
                  {/* Market context for this requirement — the label
                      itself says whether it's an asserted buyer target
                      or a derived benchmark fallback; never the same
                      wording for both, never shown without a source.
                      A "wish" target is a real range (dash, two points);
                      a "benchmark" with a mid point is three concrete
                      market segments, not a spread (dot-separated, no
                      dash implying a continuous range between them).
                      Dropped in compact for the same reason as above. */}
                  {!compact && row.indicative_price_source && (row.indicative_price_min != null || row.indicative_price_max != null) && (
                    <div className={cn(text.muted, 'hidden group-hover:block group-focus-within:block absolute left-3 right-3 top-full z-30 mt-0.5 rounded-md border border-border bg-card p-2 shadow-lg')}>
                      {(row.indicative_price_source === 'wish' ? L.price_target_wish : L.price_target_benchmark) ?? 'Target'}
                      {': '}
                      {row.indicative_price_mid != null ? (
                        <>
                          {L.price_tier_low ?? 'L'}
                          {' '}
                          {row.indicative_price_min != null ? `$${row.indicative_price_min.toLocaleString()}` : '—'}
                          {' · '}
                          {L.price_tier_mid ?? 'M'}
                          {' '}
                          {`$${row.indicative_price_mid.toLocaleString()}`}
                          {' · '}
                          {L.price_tier_high ?? 'H'}
                          {' '}
                          {row.indicative_price_max != null ? `$${row.indicative_price_max.toLocaleString()}` : '—'}
                        </>
                      ) : (
                        <>
                          {row.indicative_price_min != null ? `$${row.indicative_price_min.toLocaleString()}` : '—'}
                          {' – '}
                          {row.indicative_price_max != null ? `$${row.indicative_price_max.toLocaleString()}` : '—'}
                        </>
                      )}
                    </div>
                  )}
                </td>
                <td className={cn(
                  'sticky z-10 bg-card border-r border-border align-top',
                  selectedCollapsed ? 'p-0 py-2.5' : 'px-3 py-2.5',
                  selLeftCls, selColCls,
                )}>
                  {selectedCollapsed ? (
                    // Same mark-not-card treatment a collapsed vendor
                    // cell uses: a check when this row is decided,
                    // nothing when it isn't.
                    winner ? <CircleCheck className="mx-auto h-4 w-4 text-primary" /> : null
                  ) : winner ? (
                    compact ? (
                      // Compact: name + price only, no thumbnail — the
                      // frozen column's job here is just "what won",
                      // the thumbnail is a comfortable-mode nicety.
                      <div className="min-w-0">
                        <div className={cn(text.muted, 'leading-snug truncate')}>{winner.product_name ?? '—'}</div>
                        <div className="text-[1.1em] font-semibold tabular-nums text-foreground">
                          {(winner.reaches_quote ? winner.quoted_unit_price : winner.unit_price) != null
                            ? `$${(winner.reaches_quote ? winner.quoted_unit_price! : winner.unit_price!).toLocaleString()}`
                            : '—'}
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start gap-2">
                        {winner.image_url ? (
                          // eslint-disable-next-line @next/next/no-img-element
                          <img src={winner.image_url} alt="" className="h-10 w-10 rounded object-cover flex-shrink-0" />
                        ) : (
                          <div className="h-10 w-10 rounded bg-muted flex-shrink-0" />
                        )}
                        <div className="min-w-0">
                          <div className={cn(text.muted, 'leading-snug truncate')}>{winner.product_name ?? '—'}</div>
                          <div className="text-[1.1em] font-semibold tabular-nums text-foreground">
                            {(winner.reaches_quote ? winner.quoted_unit_price : winner.unit_price) != null
                              ? `$${(winner.reaches_quote ? winner.quoted_unit_price! : winner.unit_price!).toLocaleString()}`
                              : '—'}
                          </div>
                        </div>
                      </div>
                    )
                  ) : (
                    <span className="text-muted-foreground/60">{L.not_yet_decided ?? 'Not yet decided'}</span>
                  )}
                </td>
                {vendorColumns.map((vc) => {
                  const pick = row.picks[vc.vendor_sid];
                  const isSelected = !!selectedId && !!pick && selectedId === pick.product_sid;
                  // A row with no winner yet stays neutral — every pick
                  // equal weight, an honest "nothing decided" state, not
                  // a defect. Once a row has a winner, its other picks
                  // recede slightly so the decision reads at a glance.
                  const recede = !!winner && !pick?.is_selected;
                  if (!isOpen(vc)) {
                    // A mark, not a card: a check where this vendor's pick is the selection, a dot where it has
                    // an option (green when switching would cost less), nothing otherwise. The details are a hover away.
                    const price = pick ? priceOf(pick) : null;
                    const cheaper = !!pick && pick.switch_delta != null && pick.switch_delta < 0;
                    const tip = pick
                      ? `${pick.product_name ?? '—'}${price != null ? ` · $${price.toLocaleString()}` : ''}${pick.reaches_quote && pick.unit_price != null ? ` · ${L.price_list ?? 'List'} $${pick.unit_price.toLocaleString()}` : ''}${cheaper ? ` · ${L.cmp_cheaper_option}` : ''}`
                      : undefined;
                    return (
                      <td key={vc.vendor_sid} title={tip} className="w-[40px] min-w-[40px] max-w-[40px] px-0 py-2.5 align-top">
                        {!pick ? null : pick.is_selected ? (
                          <CircleCheck className="mx-auto h-4 w-4 text-primary" />
                        ) : (
                          <span className={cn('mx-auto mt-1 block h-2.5 w-2.5 rounded-full', cheaper ? 'bg-emerald-500' : 'bg-muted-foreground/50')} />
                        )}
                      </td>
                    );
                  }
                  if (!pick) {
                    return (
                      <td key={vc.vendor_sid} className="px-3 py-2.5 align-top text-muted-foreground/40">
                        —
                      </td>
                    );
                  }
                  return (
                    <td
                      key={vc.vendor_sid}
                      className={cn(
                        'px-3 py-2.5 align-top',
                        pick.is_selected && 'bg-primary/5',
                        recede && 'opacity-60',
                      )}
                    >
                      <div className="flex items-start gap-2">
                        {/* Its own click target, distinct from the name
                            link and the quote icon below — a decision,
                            not a navigation. Radio, not a checkbox: at
                            most one winner per row, same as the data
                            model underneath it. Clicking the current
                            winner's own radio clears the row back to
                            undecided; clicking any other radio replaces
                            the winner — never a plain toggle-off on an
                            unselected pick. */}
                        <button
                          type="button"
                          onClick={() => toggleSelection(pick)}
                          disabled={setSelection.isPending}
                          className="mt-0.5 flex-shrink-0 text-muted-foreground hover:text-primary disabled:opacity-50"
                          title={pick.is_selected ? 'Clear this selection' : 'Select this pick'}
                        >
                          {pick.is_selected ? (
                            <CircleCheck className="h-4 w-4 text-primary" />
                          ) : (
                            <Circle className="h-4 w-4" />
                          )}
                        </button>
                        {pick.image_url ? (
                          // eslint-disable-next-line @next/next/no-img-element
                          <img src={pick.image_url} alt="" className="h-10 w-10 rounded object-cover flex-shrink-0" />
                        ) : (
                          <div className="h-10 w-10 rounded bg-muted flex-shrink-0" />
                        )}
                        <div className="min-w-0 flex-1">
                          <button
                            type="button"
                            onClick={() => selectPick(pick, vc.vendor_sid, vc.vendor_name)}
                            className={cn(
                              text.muted,
                              'block text-left leading-snug',
                              isSelected ? 'text-primary underline' : 'hover:text-primary hover:underline',
                            )}
                          >
                            {pick.product_name ?? '—'}
                          </button>
                          {pick.reaches_quote && pick.quoted_unit_price != null ? (
                            <div className="mt-0.5">
                              {/* Bold + a "List" line underneath already
                                  says "this is a real quote" on its own
                                  — a checkmark on top of that was saying
                                  the same thing twice. */}
                              <div className="flex items-center gap-1">
                                <span className="text-[1.1em] font-semibold tabular-nums text-foreground">
                                  ${pick.quoted_unit_price.toLocaleString()}
                                </span>
                                {pick.quoted_response_sid && (
                                  <button
                                    type="button"
                                    onClick={() => selectVendorResponse(pick.quoted_response_sid!, vc.vendor_name)}
                                    className="text-muted-foreground hover:text-primary"
                                    title={`View ${vc.vendor_name}'s response`}
                                  >
                                    <FileText className="h-3 w-3" />
                                  </button>
                                )}
                              </div>
                              {pick.unit_price != null && (
                                <div className={cn(text.muted, 'tabular-nums')}>
                                  {L.price_list ?? 'List'} ${pick.unit_price.toLocaleString()}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="text-[1.1em] font-semibold tabular-nums text-foreground mt-0.5">
                              {pick.unit_price != null ? `$${pick.unit_price.toLocaleString()}` : '—'}
                            </div>
                          )}
                          {pick.switch_delta != null && !pick.is_selected && (
                            <div className={cn(text.muted, 'mt-0.5 tabular-nums', pick.switch_delta < 0 && status.pos)} title={L.cmp_if_switched}>
                              {pick.switch_delta < 0 ? '−' : '+'}{money(Math.abs(pick.switch_delta))}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  );
                })}
              </tr>
              </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
